(function () {
  /**
   * @typedef LaneDetail
   * @type {object}
   * @property {string} country
   * @property {HTMLElement} detail
   * @property {number} id
   * @property {HTMLElement} image
   * @property {HTMLElement|undefined} shareFacebookButton
   * @property {HTMLElement|undefined} shareTwitterButton
   * @property {boolean} unvoteCountryOnDoubleClick
   */

  /**
   *
   * @param {number} laneId
   * @return {string}
   */
  function getLaneShareLink(laneId) {
    return '{{origin}}{{path}}?lane-id={{laneId}}'
      .replace('{{origin}}', location.origin)
      .replace('{{path}}', location.pathname)
      .replace('{{laneId}}', laneId);
  }

  /**
   * @param {number} laneId
   * @param {string} text
   * @param {string[]} hashtags
   * @return {string}
   */
  function generateTwitterShareLink(laneId, text, hashtags) {
    if (typeof hashtags === 'undefined') {
      hashtags = [];
    }

    var shareTemplate =
      'https://twitter.com/intent/tweet?url={{url}}&text={{text}}&hashtags={{hashtags}}';

    return shareTemplate
      .replace('{{url}}', encodeURIComponent(getLaneShareLink(laneId)))
      .replace('{{text}}', encodeURIComponent(text))
      .replace('{{hashtags}}', encodeURIComponent(hashtags.join(',')));
  }

  /**
   * @param {string} appId
   * @param {number} laneId
   * @param {string} text
   * @return {string}
   */
  function generateFacebookShareLink(appId, laneId, text) {
    var shareTemplate =
      'https://www.facebook.com/dialog/share?app_id={{appId}}&href={{url}}&message={{text}}';

    return shareTemplate
      .replace('{{url}}', encodeURIComponent(getLaneShareLink(laneId)))
      .replace('{{appId}}', appId)
      .replace('{{text}}', encodeURIComponent(text));
  }

  /**
   * @returns {boolean}
   */
  function scrollIntoViewSupported() {
    var testElement = document.createElement('div');
    return 'scrollIntoView' in testElement;
  }

  this.DetailService = function (plugin) {
    /**
     * @type {LaneDetail|null}
     */
    this.activeDetail = null;
    this.plugin = plugin;

    /**
     *
     * @param {number} id
     * @param {string} country
     * @returns {LaneDetail}
     * @throws {Error}
     */
    this.createDetail = function (id, country) {
      var laneId = parseInt(id, 10);
      if (!laneId || laneId <= 0) {
        throw new Error('lane.invalidId');
      }
      if (!country || typeof country !== 'string') {
        throw new Error('lane.invalidCountry');
      }

      var detailElement = document.querySelector(
        '.' + this.plugin.Config.classNames.detail.base
      );
      var detailImageElement = detailElement.querySelector(
        '.' + this.plugin.Config.classNames.detailImage.base
      );
      var detailVoteButton = detailElement.querySelector(
        '.' + this.plugin.Config.classNames.vote.base
      );

      if (!detailElement || !detailImageElement || !detailVoteButton) {
        this.plugin.Logger.Debug(
          'Could not create detail because some elements are missing'
        );
        throw new Error('detail.missingImageElement');
      }

      return {
        country: country,
        detail: detailElement,
        id: laneId,
        image: detailImageElement,
        shareFacebookButton: detailElement.querySelectorAll(
          this.plugin.Config.classNames.share.facebook
        ),
        shareTwitterButton: detailElement.querySelectorAll(
          this.plugin.Config.classNames.share.twitter
        ),
        unvoteCountryOnDoubleClick: false,
        voteButton: detailVoteButton,
      };
    }.bind(this);

    this.removeOverlays = function () {
      var overlays = document.querySelectorAll(
        '.' + this.plugin.Config.classNames.overlay.base
      );
      for (
        var overlayIndex = 0;
        overlayIndex < overlays.length;
        overlayIndex++
      ) {
        overlays[overlayIndex].classList.remove(
          this.plugin.Config.classNames.overlay.active
        );
      }
    }.bind(this);

    /**
     * @param {string} className
     */
    this.showOverlayByClass = function (className) {
      this.removeOverlays();
      if (this.activeDetail) {
        var element = document.querySelector('.' + className);
        this.plugin.Logger.Debug('Trying to show overlay element:', element);
        if (element) {
          element.classList.add(this.plugin.Config.classNames.overlay.active);
        }
      }
    }.bind(this);
  };

  /**
   * @returns {null|number}
   */
  this.DetailService.prototype.GetActiveId = function () {
    return this.activeDetail ? this.activeDetail.id : null;
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.Close = function () {
    this.removeOverlays();
    if (this.activeDetail) {
      this.plugin.UnbindClick(this.activeDetail.image);
      this.activeDetail = null;
    }
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.Open = function (id, country) {
    this.activeDetail = this.createDetail(id, country);
    this.plugin.Logger.Debug('Opening detail ', this.activeDetail);

    this.plugin.BindDoubleClick(
      this.activeDetail.image,
      function () {
        this.plugin.UserData.SetUsedDoubleClick();

        if (this.activeDetail.unvoteCountryOnDoubleClick) {
          this.plugin.Voter.RemoveVotesFromCountry(country).then(
            function () {
              this.plugin.Voter.Upvote(id, country);
            }.bind(this)
          );
        } else {
          this.plugin.Voter.Upvote(id, country);
        }
      }.bind(this)
    );
    this.plugin.Voter.RegisterVoteButtonClickListener(
      this.activeDetail.voteButton
    );

    this.activeDetail.shareFacebookButton.forEach(
      function (e) {
        e.setAttribute(
          'href',
          generateFacebookShareLink(this.plugin.Config.facebookId, id, '')
        );
      }.bind(this)
    );

    this.activeDetail.shareTwitterButton.forEach(
      function (e) {
        e.setAttribute('href', generateTwitterShareLink(id, '', []));
      }.bind(this)
    );

    if (
      this.plugin.UserData.HasUsedDoubleClick() === false &&
      this.plugin.UserData.HasUsedSwipe() === false
    ) {
      this.ShowHint();
    }

    if (this.plugin.UserData.HasUploadedLane(this.activeDetail.id)) {
      this.plugin.Logger.Debug(
        'Current user has uploaded this lane',
        this.activeDetail
      );
      this.activeDetail.detail.classList.add(
        this.plugin.Config.classNames.detail.userUploaded
      );
    } else {
      this.activeDetail.detail.classList.remove(
        this.plugin.Config.classNames.detail.userUploaded
      );
    }
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.ScrollToDetail = function () {
    if (scrollIntoViewSupported() && this.activeDetail.detail) {
      var boundingRect = this.activeDetail.detail.getBoundingClientRect();
      var requiredVisibleHeight = boundingRect.height / 3;
      var offset =
        boundingRect.top > 0
          ? window.innerHeight - boundingRect.top
          : boundingRect.bottom;

      if (offset < requiredVisibleHeight) {
        this.activeDetail.detail.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.ShowHint = function () {
    this.plugin.Logger.Debug('Activating hint overlay');
    this.showOverlayByClass(this.plugin.Config.classNames.overlay.hint);
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.ShowThankMessage = function () {
    this.plugin.Logger.Debug('Activating hint overlay');
    this.showOverlayByClass(this.plugin.Config.classNames.overlay.thank);
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.ShowVoted = function () {
    this.plugin.Logger.Debug('Activating hint overlay');
    this.showOverlayByClass(this.plugin.Config.classNames.overlay.voted);
    if (this.activeDetail) {
      this.activeDetail.unvoteCountryOnDoubleClick = true;
    }
  };

  /**
   * @returns {void}
   */
  this.DetailService.prototype.ShowWarning = function () {
    this.plugin.Logger.Debug('Activating hint overlay');
    this.showOverlayByClass(this.plugin.Config.classNames.overlay.warning);
  };
}).bind(window.WorstCyclingLane)();
